import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import api from '../../services/api';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import VirtualizedList from '../common/VirtualizedList.jsx';
import { SkeletonTable } from '../common/Skeleton.jsx';
import LocationRow from './LocationRow.jsx';
import { formatLastUpdated, hasCoords } from './locationUtils';
import './LocationDashboard.css';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const EMPLOYEE_ROW_HEIGHT = 56;

function LocationDashboard() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch employee locations
  const fetchEmployeeLocations = useCallback(async () => {
    try {
      const response = await api.get('/location/ceo/employee-locations');
      setEmployees(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching employee locations:', err);
      setError('Failed to load employee locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployeeLocations();

    // Refresh every 30 seconds
    const interval = setInterval(fetchEmployeeLocations, 30000);

    return () => clearInterval(interval);
  }, [fetchEmployeeLocations]);

  const handleViewLocation = useCallback((employee) => {
    if (employee.locationEnabled && hasCoords(employee)) {
      setSelectedEmployee(employee);
    }
  }, []);

  const handleCloseMap = useCallback(() => {
    setSelectedEmployee(null);
  }, []);

  const employeeRowKey = useCallback((employee) => employee.employeeId, []);

  const renderEmployeeRow = useCallback((employee, index, style) => (
    <LocationRow employee={employee} style={style} onViewLocation={handleViewLocation} />
  ), [handleViewLocation]);

  const selectedEmployeeLastUpdated = useMemo(
    () => (selectedEmployee ? formatLastUpdated(selectedEmployee.lastUpdated) : null),
    [selectedEmployee]
  );

  if (loading) {
    return (
      <div className="location-dashboard">
        <div className="dashboard-header">
          <h2>Employee Location Tracking</h2>
        </div>
        <div className="employee-list">
          <SkeletonTable rows={8} columns={5} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="location-dashboard">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={fetchEmployeeLocations} className="btn-retry">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="location-dashboard">
      <div className="dashboard-header">
        <h2>Employee Location Tracking</h2>
        <button onClick={fetchEmployeeLocations} className="btn-refresh">
          🔄 Refresh
        </button>
      </div>

      <div className="employee-list">
        <div className="employee-table-header" role="row">
          <div className="employee-header-cell">Name</div>
          <div className="employee-header-cell">Status</div>
          <div className="employee-header-cell">Location Status</div>
          <div className="employee-header-cell">Last Updated</div>
          <div className="employee-header-cell">Action</div>
        </div>

        {employees.length === 0 ? (
          <div className="no-employees">
            <p>No employees found</p>
          </div>
        ) : (
          <div className="employee-list-body" role="rowgroup">
            <VirtualizedList
              items={employees}
              itemKey={employeeRowKey}
              rowHeight={EMPLOYEE_ROW_HEIGHT}
              renderRow={renderEmployeeRow}
              className="employee-virtual-list scrollbar-thin"
              style={{ height: '100%' }}
            />
          </div>
        )}
      </div>

      {/* Map Modal — a single interactive Leaflet map, never virtualized */}
      {selectedEmployee && (
        <div className="map-modal">
          <div className="map-modal-content">
            <div className="map-modal-header">
              <h3>{selectedEmployee.name}'s Location</h3>
              <button onClick={handleCloseMap} className="btn-close">
                ✕
              </button>
            </div>
            <div className="map-container">
              <MapContainer
                center={[selectedEmployee.latitude, selectedEmployee.longitude]}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[selectedEmployee.latitude, selectedEmployee.longitude]}>
                  <Popup>
                    <strong>{selectedEmployee.name}</strong>
                    <br />
                    Employee is here
                    <br />
                    <small>Last updated: {selectedEmployeeLastUpdated}</small>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
            <div className="map-info">
              <p>
                <strong>Coordinates:</strong> {selectedEmployee.latitude.toFixed(6)}, {selectedEmployee.longitude.toFixed(6)}
              </p>
              <p>
                <strong>Last Updated:</strong> {selectedEmployeeLastUpdated}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LocationDashboard;
